import { ZodDiscriminatedUnion, type ZodObject, type z } from 'zod'

type DiscriminatedUnionOptions<S> =
  S extends ZodDiscriminatedUnion<infer A, infer B>
    ? A extends ReadonlyArray<ZodObject<any>>
      ? A
      : B extends ReadonlyArray<ZodObject<any>>
        ? B
        : never
    : never

type DiscriminatedUnionKeys<Options extends ReadonlyArray<ZodObject<any>>> =
  Options[number] extends ZodObject<infer Shape> ? Extract<keyof Shape, string> : never

/**
 * Returns the root keys of a Zod object schema as a typed array.
 *
 * Supports an optional compile-time prefix that is reflected in the return type.
 * Example: prefix: 'company.' -> ("company.id" | "company.name" | ... )[]
 */
export function zodObjectRootKeys<T extends z.ZodRawShape>(schema: z.ZodObject<T>): (keyof T)[]
export function zodObjectRootKeys<T extends z.ZodRawShape, P extends string>(
  schema: z.ZodObject<T>,
  prefix: P,
): `${P}${Extract<keyof T, string>}`[]
export function zodObjectRootKeys<S extends ZodDiscriminatedUnion<any, any>>(
  schema: S,
): DiscriminatedUnionKeys<DiscriminatedUnionOptions<S>>[]
export function zodObjectRootKeys<S extends ZodDiscriminatedUnion<any, any>, P extends string>(
  schema: S,
  prefix: P,
): `${P}${DiscriminatedUnionKeys<DiscriminatedUnionOptions<S>>}`[]
export function zodObjectRootKeys<T extends z.ZodRawShape, P extends string>(
  schema: z.ZodObject<T> | ZodDiscriminatedUnion<any, any>,
  prefix?: P,
) {
  if (schema instanceof ZodDiscriminatedUnion) {
    const options = ((): ReadonlyArray<ZodObject<any>> => {
      const anySchema = schema as unknown as {
        options?: ReadonlyArray<ZodObject<any>>
        _def?: { options?: ReadonlyArray<ZodObject<any>> }
        def?: { options?: ReadonlyArray<ZodObject<any>> }
      }
      return anySchema._def?.options ?? anySchema.def?.options ?? anySchema.options ?? []
    })()

    const keys = [...new Set(options.flatMap(option => Object.keys(getObjectShape(option))))] as string[]
    if (!prefix) return keys as unknown as DiscriminatedUnionKeys<ReadonlyArray<ZodObject<any>>>[]
    return keys.map(k => `${prefix}${k}`) as unknown as `${P}${DiscriminatedUnionKeys<ReadonlyArray<ZodObject<any>>>}`[]
  }

  // Zod v3 exposes `shape` as an object, Zod v4 uses `_def.shape()` returning the shape.
  // Support both to be robust across versions.
  const shape = getObjectShape(schema)
  const keys = Object.keys(shape) as Extract<keyof T, string>[]
  if (!prefix) return keys as unknown as (keyof T)[]
  return keys.map(k => `${prefix}${k}`) as unknown as `${P}${Extract<keyof T, string>}`[]
}

function getObjectShape<T extends z.ZodRawShape>(schema: z.ZodObject<T>): T {
  const anySchema = schema as unknown as { shape?: T; _def?: { shape?: () => T } }
  if (typeof anySchema._def?.shape === 'function') return anySchema._def.shape()
  if (anySchema.shape) return anySchema.shape
  return {} as T
}
